<script setup>
import { ref, reactive } from 'vue'
import GridList from '../others/GridList.vue'
import SlideDetailView from '../others/SlideDetailView.vue'
import CategoryDetails from '../categories/CategoryDetails.vue'
import FileUploader from '@/presentation/components/input/FileUploader.vue'
import ApiService from '@/shared/utils/apiService'
import Events from '@/events/events'
import { EVENTS } from '@/shared/constants/commons'
import Modal from '@/shared/utils/modal'

const cols = [
  {
    name: 'Nom',
    id: 'name',
    value(el) {
      return el.name
    },
  },

  {
    name: '',
    id: 'action',
    icon: () => '/images/icons/eye.png',
    onClick(el) {
      detailData.value = el
    },
    value() {},
  },
]

const detailData = ref(null)
const isAdding = ref(false)
const addLoading = ref(false)
const gridRef = ref(null)

const addForm = reactive({
  name: '',
  imageUrl: '',
  active: true,
})

function handleImageUpload(fileData) {
  addForm.imageUrl = fileData.url
}

function showAddModal() {
  addForm.name = ''
  addForm.imageUrl = ''
  addForm.active = true
  isAdding.value = true
}

async function createCategory() {
  if (!addForm.name) return Modal.show('Erreur', 'Le nom est obligatoire')

  addLoading.value = true
  try {
    addForm.iconUrl = addForm.imageUrl
    const res = await ApiService.post('categories/add?group=ultraxeno', addForm)
    if (res.success) {
      isAdding.value = false
      Events.dispatch(EVENTS.NOTIFICATION, {
        type: 'success',
        message: 'Catégorie créée avec succès',
      })
      refresh()
    }
  } catch (err) {
    Modal.show('Erreur', err.message || 'La création a échoué')
  } finally {
    addLoading.value = false
  }
}

function refresh() {
  // Hack to refresh GridList items by triggering a search or similar
  // Since GridList uses DataFetcher/ListFetcher internally based on dataKey
  Events.dispatch('refresh_list_categories')
}

const buttons = [
  {
    title: '+ Ajouter une catégorie',
    onClick: showAddModal,
  },
]
</script>

<template>
  <div class="p-5 h-full overflow-auto">
    <GridList
      ref="gridRef"
      title="Catégories"
      dataKey="categories"
      hasSearch
      :cols="cols"
      :buttons="buttons"
      refreshOnEvents="refresh_list_categories"
      limit="100"

      :elClickHanler="(el)=>{
        detailData = el
      }"
      :cardConfig="{
        imageField: 'imageUrl',
        titleField: 'name',
        descriptionField: 'id',
      }"
      :gridCols="{ sm: 1, md: 2, lg: 3, xl: 4 }"
    ></GridList>
  </div>

  <!-- Add Category Modal -->
  <SlideDetailView :isVisible="isAdding" @hide="isAdding = false">
    <div class="p-8 space-y-6">
      <h2 class="text-2xl font-bold title-color">Nouvelle Catégorie</h2>
      <div class="space-y-4">
        <div class="space-y-1">
          <label class="text-sm font-medium text-gray-700">Nom</label>
          <input
            v-model="addForm.name"
            type="text"
            placeholder="Ex: Pizza, Burger..."
            class="w-full h-11 px-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
          />
        </div>
        <div class="space-y-1">
          <label class="text-sm font-medium text-gray-700">Image</label>
          <FileUploader
            placeHolder="Choisir une image pour la catégorie"
            :acceptedFilesTypes="['image/*']"
            @onFileChangedV2="handleImageUpload"
            :fileId="addForm.imageUrl"
          />
          <div v-if="addForm.imageUrl" class="mt-3">
            <img
              :src="addForm.imageUrl"
              alt="Preview"
              class="h-20 w-20 object-contain rounded-lg border"
            />
          </div>
        </div>
        <div class="flex items-center gap-2 py-2">
          <input
            type="checkbox"
            v-model="addForm.active"
            id="active"
            class="w-4 h-4 text-primary rounded border-gray-300"
          />
          <label for="active" class="text-sm text-gray-700"
            >Activer immédiatement</label
          >
        </div>
      </div>
      <div class="flex gap-3 pt-6">
        <button
          @click="isAdding = false"
          class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex-1"
        >
          Annuler
        </button>
        <button
          @click="createCategory"
          :disabled="addLoading"
          class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex-1"
        >
          <span
            v-if="addLoading"
            class="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"
          ></span>
          Créer
        </button>
      </div>
    </div>
  </SlideDetailView>

  <!-- Detail/Edit Side Panel -->
  <SlideDetailView :isVisible="!!detailData" @hide="detailData = null">
    <CategoryDetails
      :categoryData="detailData"
      @updated="
        () => {
          refresh()
          detailData = null
        }
      "
      @deleted="
        () => {
          refresh()
          detailData = null
        }
      "
    />
  </SlideDetailView>
</template>
